import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { ConstraintBar } from "@/components/constraints/ConstraintBar";
import { CostOfDeviationStack } from "@/components/economics/CostOfDeviationStack";
import { ValueStateFunnel } from "@/components/economics/ValueStateFunnel";

export default function Dashboard() {
  const { data, error } = usePolling(() => api.get("/dashboard").then((r) => r.data), 6000);

  if (error) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="dashboard-error">Could not load dashboard. Retrying…</div>;
  if (!data) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="dashboard-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold font-heading text-[var(--oim-text)]">Site View</h1>
        {data.active_scenarios.length > 0 && (
          <div className="text-xs mt-1" style={{ color: "#FF6B00" }} data-testid="active-scenario-banner">
            Active injection: {data.active_scenarios.join(", ")}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-kpi-strip">
        {data.kpi_strip.map((k) => (
          <div key={k.label} className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
            <div className="text-[10px] uppercase tracking-widest text-[var(--oim-text-dim)] font-heading">{k.label}</div>
            <div className="font-mono-data text-2xl font-bold text-[var(--oim-text)] mt-1">
              {k.value.toLocaleString("en-IN")} <span className="text-xs text-[var(--oim-text-muted)]">{k.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 space-y-4">
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
            <ConstraintBar constraint={data.constraint} testId="constraint-binding-dashboard" />
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
            <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Cost of Deviation</div>
            <CostOfDeviationStack bars={data.cost_of_deviation_stack} testId="cost-of-deviation-stack-dashboard" />
          </div>
        </div>
        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
            <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Value State Funnel</div>
            <ValueStateFunnel funnel={data.value_state_funnel} testId="value-state-funnel-dashboard" />
          </div>
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }} data-testid="safety-summary-card">
            <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-2">Safety Summary</div>
            <div className="flex gap-4 font-mono-data text-sm">
              <span style={{ color: "var(--oim-green)" }}>{data.safety_summary.count_normal} normal</span>
              <span style={{ color: "var(--oim-gold)" }}>{data.safety_summary.count_attention} attention</span>
            </div>
            <div className="text-[10px] text-[var(--oim-text-dim)] mt-1">not monetised</div>
          </div>
        </div>
        <div className="lg:col-span-full rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }} data-testid="confidence-coverage-panel">
          <div className="text-sm font-heading font-bold text-[var(--oim-text)] mb-3">Confidence & Coverage</div>
          <div className="flex gap-6 font-mono-data text-sm mb-2">
            <span className="text-[var(--oim-text)]">Modelled {data.confidence_coverage.modelled_pct}%</span>
            <span className="text-[var(--oim-text-muted)]">Measured {data.confidence_coverage.measured_pct}%</span>
            <span className="text-[var(--oim-text-dim)]">Estimated {data.confidence_coverage.estimated_pct}%</span>
          </div>
          <div className="text-xs text-[var(--oim-text-muted)]">{data.confidence_coverage.note}</div>
        </div>
      </div>
    </div>
  );
}
